import gsap from 'gsap';
import { Mask } from 'maska';
import React, { forwardRef, useEffect, useRef, useState, } from 'react';
import styled, { css } from 'styled-components';
import { Icon } from '../Icon';
import { InputContainer, InputElement, InputWrapper } from './Input';
const PasswordContainer = styled(InputContainer) `
  display: flex;
`;
const Fields = styled.div `
  --password-visible-position: 200%;
  --password-hidden-position: 50%;
  position: relative;
  flex-grow: 1;
`;
const StyledInputElement = styled(InputElement) `
  mask-position: var(--password-visible-position);
  mask-size: 200%;
  mask-image: linear-gradient(-90deg, transparent 0%, black 25%, black 75%, transparent 100%);
  mask-repeat: no-repeat;

  ${({ $isVisible }) => !$isVisible &&
    css `
      pointer-events: none;
    `}
`;
const PasswordElement = styled(StyledInputElement) `
  position: absolute;
  top: 0;
  mask-position: var(--password-hidden-position);

  ${({ $isMask }) => $isMask &&
    css `
      color: transparent;
      caret-color: ${({ theme }) => theme.primary.background};
    `}

  :not(:placeholder-shown) {
    font-family: Verdana, sans-serif;
    font-size: 13px;
    letter-spacing: 0.125em;
  }

  ${({ $isVisible }) => !$isVisible &&
    css `
      pointer-events: none;
    `}
`;
const PasswordElementDisplay = styled(StyledInputElement) `
  position: absolute;
  top: 0;
  pointer-events: none;
  user-select: none;
  mask-position: var(--password-hidden-position);

  :not(:placeholder-shown) {
    font-family: Verdana, sans-serif;
    font-size: 13px;
    letter-spacing: 0.125em;
  }
`;
const Toggle = styled.div `
  cursor: pointer;
  padding: 6px;
  margin: -8px -6px -8px auto;
`;
const Eye = styled(Icon.AnimationEye) `
  --eye-line-background-color: var(--input-background, ${({ theme }) => theme.secondary.blendToBackground(75)});

  svg {
    --icon-color: ${({ theme }) => theme.secondary.blendToBackground(500)};
  }

  &:hover {
    svg {
      --icon-color: ${({ theme }) => theme.secondary.blendToBackground(800)};
    }
  }
`;
export const PasswordInput = forwardRef((props, ref) => {
    const [isVisibleState, setIsVisibleState] = useState(false);
    const [isHoveredState, setIsHoveredState] = useState(props.isHovered ?? false);
    const [isFocussedState, setIsFocussedState] = useState(props.isFocussed ?? false);
    const [isFilledState, setIsFilledState] = useState(props.isFilled ?? false);
    const [currentValue, setCurrentValue] = useState(props.value ?? '');
    const onChange = props.onChange ??
        (() => {
            /* NOOP */
        });
    const onFocus = props.onFocus ??
        (() => {
            /* NOOP */
        });
    const onBlur = props.onBlur ??
        (() => {
            /* NOOP */
        });
    const onKeyDown = props.onKeyDown ??
        (() => {
            /* NOOP */
        });
    const inputRef = useRef(null);
    const passwordRef = useRef(null);
    const fieldsRef = useRef(null);
    const maskRef = useRef(new Mask({ mask: props.mask }));
    const styleProps = {
        $isVisible: isVisibleState,
        $isHovered: props.isHovered ?? isHoveredState,
        $isFocussed: props.isFocussed ?? isFocussedState,
        $isFilled: props.isFilled ?? isFilledState,
        $isDisabled: props.isDisabled ?? false,
        $isPressed: props.isPressed ?? false,
        $hasError: props.hasError ?? false,
        $size: props.size ?? 'default',
        $variant: props.variant ?? 'default',
        $isMask: !!props.mask,
    };
    const handleClick = () => {
        if (props.isDisabled) {
            return;
        }
        if (props.onClick) {
            props.onClick();
        }
        setIsFocussedState(true);
    };
    const handleVisibleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const isVisible = !isVisibleState;
        if (props.onVisibleChange) {
            props.onVisibleChange(isVisible);
        }
        if (isVisible) {
            gsap.to(fieldsRef.current, {
                '--password-hidden-position': '-100%',
                duration: 0.4,
            });
            gsap.to(fieldsRef.current, {
                '--password-visible-position': '50%',
                duration: 0.4,
                delay: 0.1,
            });
        }
        else {
            gsap.set(fieldsRef.current, {
                '--password-hidden-position': '200%',
            });
            gsap.to(fieldsRef.current, {
                '--password-visible-position': '-100%',
                duration: 0.4,
            });
            gsap.to(fieldsRef.current, {
                '--password-hidden-position': '50%',
                duration: 0.4,
                delay: 0.1,
                onComplete: (target) => {
                    gsap.set(target, {
                        '--password-visible-position': '200%',
                        '--password-hidden-position': '50%',
                    });
                },
                onCompleteParams: [fieldsRef.current],
            });
        }
        setIsVisibleState(isVisible);
    };
    const handleHoverChange = (value) => {
        if (props.isDisabled) {
            return;
        }
        if (props.onHoverChange) {
            props.onHoverChange(value);
        }
        setIsHoveredState(value);
    };
    const handleFocusChange = (value) => {
        if (props.isDisabled) {
            return;
        }
        if (props.onFocusChange) {
            props.onFocusChange(value);
        }
        setIsFocussedState(value);
    };
    const handleKeyPress = (event) => {
        if (event.key === 'Enter' && props.autoBlur) {
            inputRef.current?.blur();
        }
    };
    useEffect(() => {
        const currentRef = isVisibleState ? inputRef : passwordRef;
        if (props.isFocussed ?? isFocussedState) {
            currentRef.current?.focus();
        }
        else {
            currentRef.current?.blur();
        }
    }, [isFocussedState, props.isFocussed, isVisibleState]);
    useEffect(() => {
        if (currentValue !== props.value) {
            setCurrentValue(props.value ?? '');
        }
    }, [props.value]);
    useEffect(() => {
        if (currentValue) {
            setIsFilledState(currentValue.length > 0);
        }
        if (onChange && currentValue !== props.value) {
            onChange(currentValue);
        }
    }, [currentValue]);
    const maskProps = !!props.mask
        ? {
            value: maskRef.current.masked(currentValue ?? ''),
            onChange: (e) => setCurrentValue(maskRef.current.unmasked(e.target.value)),
            placeholder: maskRef.current.masked(props.placeholder ?? ''),
        }
        : {
            value: currentValue,
            onChange: (e) => setCurrentValue(e.target.value),
            placeholder: props.placeholder ?? '',
        };
    return (React.createElement(InputWrapper, { ref: ref, style: props.style, className: props.className, onMouseEnter: () => handleHoverChange(true), onMouseMove: () => handleHoverChange(true), onMouseLeave: () => handleHoverChange(false), onClick: handleClick, "data-error": typeof props.hasError === 'string' ? props.hasError : undefined, "$hasError": props.hasError, "$isFocussed": props.isFocussed ?? isFocussedState, "$isHovered": props.isHovered ?? isHoveredState, "$isPressed": props.isPressed, "$isDisabled": props.isDisabled, "$isFilled": props.isFilled ?? isFilledState, "$size": props.size, "$variant": props.variant },
        React.createElement(PasswordContainer, { "$hasError": props.hasError, "$isFocussed": props.isFocussed ?? isFocussedState, "$isHovered": props.isHovered ?? isHoveredState, "$isPressed": props.isPressed, "$isDisabled": props.isDisabled, "$isFilled": props.isFilled ?? isFilledState, "$size": props.size, "$variant": props.variant },
            React.createElement(Fields, { ref: fieldsRef },
                React.createElement(StyledInputElement, { onKeyPress: handleKeyPress, autoFocus: props.autoFocus, disabled: props.isDisabled ?? false, type: "text", readOnly: props.readOnly ?? false, maxLength: props.maxLength, "$isVisible": styleProps.$isVisible, ...maskProps, onBlur: (e) => {
                        handleFocusChange(false);
                        onBlur(e);
                    }, onFocus: (e) => {
                        handleFocusChange(true);
                        onFocus(e);
                    }, onKeyDown: (e) => {
                        onKeyDown(e);
                    }, ref: inputRef, autoComplete: props.autoComplete }),
                React.createElement(PasswordElement, { onKeyPress: handleKeyPress, autoFocus: props.autoFocus, disabled: props.isDisabled ?? false, type: "password", readOnly: props.readOnly ?? false, maxLength: props.maxLength, "$isVisible": styleProps.$isVisible, "$isMask": styleProps.$isMask, ...maskProps, onBlur: (e) => {
                        handleFocusChange(false);
                        onBlur(e);
                    }, onFocus: (e) => {
                        handleFocusChange(true);
                        onFocus(e);
                    }, onKeyDown: (e) => {
                        onKeyDown(e);
                    }, ref: passwordRef, autoComplete: props.autoComplete }),
                !!props.mask && (React.createElement(PasswordElementDisplay, { type: "text", readOnly: true, "$isVisible": styleProps.$isVisible, value: !!props.mask ? maskProps.value.replaceAll(/[^-]/g, '•') : maskProps.value.replaceAll(/./g, '•'), placeholder: props.placeholder }))),
            React.createElement(Toggle, { onClick: handleVisibleClick },
                React.createElement(Eye, { line: !isVisibleState }))),
        props.children));
});
//# sourceMappingURL=Password.js.map